// SYNC: keep in sync with select-web-client-patch/src/components/common/CookieConsent/cookieConsentConfig.ts
import React, { useEffect, useRef, useState } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { CookieDomain, privacyPolicyLink } from "../../Constants";
import { toI18N } from "../../i18n";
import { logEvent } from "../../services/GoogleAnalyticsService";
import { hasConsentDecision, isNativeAppSession } from "../../utils/consentUtils";
import CookieConsentSheet from "./CookieConsentSheet";

function onAnalyticsAccepted() {
	// gtag.js is already loaded (denied) from public/index.html — grant it in
	// place rather than injecting a second script tag or reloading the page.
	if (typeof window !== "undefined" && window.gtag) {
		window.gtag("consent", "update", { analytics_storage: "granted" });
	}
}

function onAnalyticsRejected() {
	if (typeof window !== "undefined" && window.gtag) {
		window.gtag("consent", "update", { analytics_storage: "denied" });
	}
}

// Event name and parameter must stay identical to the web client's
// COOKIE_CONSENT.DECISION event — the two surfaces aggregate into one opt-in
// rate, so any drift here silently splits the metric.
function reportDecision(analyticsGranted: boolean) {
	logEvent("cookie_consent_decision", {
		consent_choice: analyticsGranted ? "accept_all" : "essential_only",
	});
}

// The library runs headlessly (`autoShow: false`): it still owns consent
// storage, the preferences modal and the consent callbacks, but the first
// layer is our own sheet so the approved design is reproducible.
export default function CookieConsentBanner() {
	const [open, setOpen] = useState(false);
	const decided = useRef(false);

	useEffect(() => {
		// Never ask inside the native app's sign-up flow: that session is
		// ephemeral by design, so the banner would block sign-up and reappear on
		// every attempt while nothing it wrote could ever persist.
		if (isNativeAppSession()) return;

		// Only ask when no decision exists anywhere on the domain. A visitor who
		// chose on the community landing page must never be asked twice —
		// re-asking a refusal is the pattern regulators pursue, and it costs
		// conversions.
		if (hasConsentDecision()) return;

		CookieConsent.run({
			cookie: {
				domain: CookieDomain || undefined,
			},

			// The first layer is our own sheet (CookieConsentSheet), so the
			// library must never render its own consent modal. Page interaction
			// is blocked by that sheet's scrim rather than by
			// `disablePageInteraction`.
			autoShow: false,

			categories: {
				necessary: {
					enabled: true,
					readOnly: true,
				},
				analytics: {
					enabled: true,
				},
			},

			guiOptions: {
				consentModal: {
					layout: "box",
					position: "bottom center",
				},
			},

			language: {
				default: "en",
				translations: {
					en: {
						consentModal: {
							title: toI18N("cookie_banner_title"),
							description: `${toI18N("cookie_banner_description")} <a href="${privacyPolicyLink}" target="_blank">${toI18N("cookie_privacy_policy")}</a>`,
							acceptAllBtn: toI18N("cookie_accept_all"),
							acceptNecessaryBtn: toI18N("cookie_essential_only"),
							showPreferencesBtn: toI18N("cookie_manage_preferences"),
						},
						preferencesModal: {
							title: toI18N("cookie_preferences_title"),
							savePreferencesBtn: toI18N("cookie_save_preferences"),
							sections: [
								{
									title: toI18N("cookie_usage_title"),
									description: toI18N("cookie_usage_description"),
								},
								{
									title: toI18N("cookie_necessary_title"),
									description: toI18N("cookie_necessary_description"),
									linkedCategory: "necessary",
								},
								{
									title: toI18N("cookie_analytics_title"),
									description: toI18N("cookie_analytics_description"),
									linkedCategory: "analytics",
								},
							],
						},
					},
				},
			},

			// Deliberately `onFirstConsent`, not `onConsent`: the library fires
			// `onConsent` on the first action AND on every page load, so
			// reporting from there would re-count returning visitors once per
			// load and bias the opt-in rate towards whoever browses the most.
			// `onFirstConsent` fires exactly once per person.
			onFirstConsent: ({ cookie }) => {
				// Also covers a first decision made inside the preferences modal.
				decided.current = true;
				setOpen(false);
				reportDecision(cookie.categories.includes("analytics"));
			},

			onConsent: ({ cookie }) => {
				if (cookie.categories.includes("analytics")) {
					onAnalyticsAccepted();
				} else {
					onAnalyticsRejected();
				}
			},

			onModalHide: ({ modalName }) => {
				// Preferences modal dismissed without choosing — ask again.
				if (modalName !== "preferencesModal") return;
				if (!decided.current && !hasConsentDecision()) setOpen(true);
			},

			onChange: ({ cookie }) => {
				if (cookie.categories.includes("analytics")) {
					onAnalyticsAccepted();
				} else {
					onAnalyticsRejected();
				}
			},
		});

		setOpen(true);
	}, []);

	const decide = (categories: string | string[]) => {
		decided.current = true;
		setOpen(false);
		CookieConsent.acceptCategory(categories);
	};

	return (
		<CookieConsentSheet
			open={open}
			onAcceptAll={() => decide("all")}
			onEssentialOnly={() => decide([])}
			onManageSettings={() => {
				// Closed first: leaving our panel up would sit on top of the
				// library's preferences modal.
				setOpen(false);
				CookieConsent.showPreferences();
			}}
		/>
	);
}
